// src/pages/NotFound.js
import React from 'react';
import { Typography, Box, Button, Container } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import BoltIcon from '@mui/icons-material/Bolt';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';

// Animations
const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0, -20px, 0);
  }
  70% {
    transform: translate3d(0, -10px, 0);
  }
  90% {
    transform: translate3d(0, -4px, 0);
  }
`;

const flicker = keyframes`
  0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% {
    opacity: 0.99;
  }
  20%, 21.999%, 63%, 63.999%, 65%, 69.999% {
    opacity: 0.4;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
  color: 'white',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
}));

const PowerIcon = styled(PowerOffIcon)(({ theme }) => ({
  fontSize: '8rem',
  animation: `${flicker} 2s infinite linear`,
  color: '#fbbf24',
  marginBottom: theme.spacing(2),
}));

const FloatingBolt = styled(BoltIcon)(({ theme }) => ({
  position: 'absolute',
  color: '#ffd93d',
  animation: `${rotate} 3s linear infinite`,
  opacity: 0.7,
}));

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  animation: `${bounce} 2s infinite`,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(1.5, 4),
  borderRadius: '25px',
  background: 'linear-gradient(45deg, #1d4ed8 30%, #fbbf24 90%)',
  border: 0,
  color: 'white',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  '&:hover': {
    background: 'linear-gradient(45deg, #2563eb 30%, #f59e0b 90%)',
    transform: 'scale(1.05)',
  },
  transition: 'all 0.3s ease',
}));

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <StyledContainer maxWidth={false}>
      {/* Floating energy bolts */}
      <FloatingBolt sx={{ top: '10%', left: '10%', fontSize: '3rem' }} />
      <FloatingBolt sx={{ top: '20%', right: '15%', fontSize: '2rem', animationDelay: '1s' }} />
      <FloatingBolt sx={{ bottom: '30%', left: '20%', fontSize: '2.5rem', animationDelay: '2s' }} />
      <FloatingBolt sx={{ bottom: '10%', right: '10%', fontSize: '4rem', animationDelay: '0.5s' }} />

      <Box sx={{ zIndex: 1 }}>
        <PowerIcon />

        <AnimatedTitle variant="h1" component="h1">
          4⚡4
        </AnimatedTitle>

        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: '300' }}>
          Oops! Energy Supply Interrupted
        </Typography>

        <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
          It looks like this page has gone off-grid! 🔌
        </Typography>

        <Typography variant="body1" sx={{ mb: 1, maxWidth: '600px', mx: 'auto', lineHeight: 1.6 }}>
          Our energy management system couldn't locate the requested resource.
          Perhaps it's been optimized away during our demand response program,
          or maybe it's just taking a well-deserved break during off-peak hours.
        </Typography>

        <Typography variant="body2" sx={{ mb: 4, opacity: 0.8, fontStyle: 'italic' }}>
          Don't worry - our smart grid is working on flexible solutions! ⚡
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          <ElectricBoltIcon sx={{ mr: 1, color: '#ffd93d' }} />
          <Typography variant="body2">
            <strong>Pro Tip:</strong> Try adjusting your navigation flexibility settings
          </Typography>
          <ElectricBoltIcon sx={{ ml: 1, color: '#ffd93d' }} />
        </Box>

        <StyledButton
          onClick={handleGoHome}
          startIcon={<BoltIcon />}
        >
          Recharge & Go Home
        </StyledButton>
      </Box>
    </StyledContainer>
  );
};

export default NotFound;
